import React, { useState } from 'react';
import {
    checkIfKnownIbanBceCombination,
    formatNationalRegister,
    formatNumberAddress,
    formatNumberCompany,
    formatPhone,
    handleDeleteFile,
    handleFileValidation,
    isValidNationalRegisterNumber,
} from './FormHelper';
import AddressPartialForm from './PartialForm/AddressPartialForm';
import ContactPartialForm from './PartialForm/ContactPartialForm';
import SectorPartialForm from './PartialForm/SectorPartialForm';
import { ITuteurFormData } from './Types/ITuteurFormData';
import { PrimeTuteurService } from '../../Services';
import { FloatLabel } from '../../Components';
import CheckboxField from './PartialForm/CheckboxField';
import FileUploadField from './PartialForm/FileUploadField';
import IdentificationTutorPartialForm from './PartialForm/IdentificationTutorPartialForm';
import { IbanOwnerType } from './Types';
import { validateTuteurForm } from './FormValidator';
import { PrimeSuccessWidget } from './PrimeSuccessWidget';
import { WidgetHelper } from '../../Helpers/WidgetHelpers';
import { useTranslation } from '../../Helpers';

interface Props {
    language: string;
    translationJson: string;
}

const PrimeTuteurForm = ({ language, translationJson }: Props) => {
    const [data, setData] = useState<ITuteurFormData>({
        addressLocality: '',
        addressNumber: '',
        addressPostal: '',
        addressStreet: '',
        bic: '',
        commissionCompany: '',
        denominationCompany: '',
        iban: '',
        ibanOwner: IbanOwnerType.OwnerAsker,
        isIbanOk: false,
        isKnownCombinationCompanyIBAN: false,
        mail: '',
        mailConfirmation: '',
        nationalRegisterTrainee: '',
        nationalRegisterTraineeIsValid: false,
        nationalRegisterTutor: '',
        nationalRegisterTutorIsValid: false,
        numberCompany: '',
        phone: '',
        phoneConfirmation: '',
        sectorCompany: '',
        socialCompany: '',
        surname: '',
        workCompany: '',
        name: '',
        schoolAttestationFile: null as any,
        bankAttestationFile: null as any,
        dataAreCorrect: false,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof ITuteurFormData, string>>
    >({});
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const translations = useTranslation(translationJson) as Record<
        string,
        string
    >;

    const borderRadius = {
        borderRadius: '5px',
    } as React.CSSProperties;

    const handleChange = <K extends keyof ITuteurFormData>(
        field: K,
        value: ITuteurFormData[K]
    ) => {
        setData((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleChangeNumberCompany = (value: string) => {
        const formatted = formatNumberCompany(value);
        setData((prev: any) => ({ ...prev, numberCompany: formatted }));
    };

    const handleBlurNumberCompany = async () => {
        if (data.iban && data.numberCompany && data.isIbanOk) {
            const isKnown = await checkIfKnownIbanBceCombination(
                data.iban,
                data.numberCompany
            );
            setData((prev: any) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: isKnown,
            }));
        } else {
            setData((prev: any) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: false,
            }));
        }
    };

    const handleBlurNumberCompanyVoid = () => {
        void handleBlurNumberCompany();
    };

    const handleChangeNumberAddress = (value: string) => {
        const formatted = formatNumberAddress(value);
        setData((prev: any) => ({ ...prev, addressNumber: formatted }));
    };

    const handleChangePhone = (value: string, field: string) => {
        const phoneFormatted = formatPhone(value);
        setData((prev: any) => ({ ...prev, [field]: phoneFormatted }));
    };

    const handleSelectSector = (event: { sector: string; work: string }) => {
        setData((prev: any) => ({
            ...prev,
            sectorCompany: event.sector,
            workCompany: event.work,
        }));
    };

    const handleChangeNationalRegisterNumber =
        <T extends object>(field: keyof T, validityField: keyof T) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = event.target.value;
            const cleanValue = formatNationalRegister(rawValue);
            const isValid = isValidNationalRegisterNumber(cleanValue);

            setData((prev) => ({
                ...prev,
                [field]: cleanValue,
                [validityField]: isValid,
            }));
        };

    const onChangeTutorNiss =
        handleChangeNationalRegisterNumber<ITuteurFormData>(
            'nationalRegisterTutor',
            'nationalRegisterTutorIsValid'
        );

    const onChangeTraineeNiss =
        handleChangeNationalRegisterNumber<ITuteurFormData>(
            'nationalRegisterTrainee',
            'nationalRegisterTraineeIsValid'
        );

    const handleChangeCommission = (value: string) => {
        const digits = value.replace(/\D/g, '');
        const prefix = translations['Site.Prime.CommissionValue'] + ' ';

        const formatted =
            digits.length === 0
                ? ''
                : digits.length < 4
                  ? prefix + digits.padEnd(3, '0').substring(0, 3)
                  : prefix +
                    `${digits.substring(0, 3)}.${digits.substring(3, 5)}`;

        setData((prev) => ({ ...prev, commissionCompany: formatted }));
    };

    const handleChangeFiles = <K extends keyof ITuteurFormData>(
        e: React.ChangeEvent<HTMLInputElement>,
        name: K
    ) => {
        if (e.target.files && e.target.files[0]) {
            handleFileValidation<ITuteurFormData>(e.target.files[0], {
                fieldName: name,
                setData,
            });
        }
    };

    const onFileDelete = () => {
        handleDeleteFile<ITuteurFormData>('bankAttestationFile', setData);
    };
    const handleSubmit = () => {
        void handleSubmitAsync();
    };

    const handleSubmitAsync = async () => {
        const validationErrors = validateTuteurForm(data, translations);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            try {
                if (typeof window.getCaptchaToken !== 'function') {
                    throw new Error('Captcha is not available');
                }

                const captchaToken = await window.getCaptchaToken(
                    'prime_tuteur_request'
                );

                if (!captchaToken) {
                    throw new Error('Captcha token is empty');
                }

                const response = await PrimeTuteurService.pushData(
                    data,
                    captchaToken
                );

                if (
                    response &&
                    response.status === 200 &&
                    (await response.json()) === true
                ) {
                    setIsSubmit(true);
                    setIsSuccess(true);
                } else {
                    setIsSubmit(true);
                    setIsSuccess(false);
                }
            } catch (error) {
                console.error('Erreur lors de la soumission:', error);
                setIsSubmit(true);
                setIsSuccess(false);
            }
        }
    };

    if (isSubmit) {
        return (
            <PrimeSuccessWidget
                isSuccess={isSuccess}
                dictSuccessText1={translations['Site.Prime.SuccessTextFpie1']}
                dictSuccessText2=""
                dictSuccessContact1={
                    translations['Site.Prime.SuccessContactEmployeur']
                }
                dictSuccessContact2=""
                dictFailLabel={''}
                dictSuccessPhone={''}
                dictSuccessSignature1={''}
                dictSuccessSignature2={''}
            />
        );
    }

    return (
        <form
            id="primeForm"
            data-skip-captcha="true"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <IdentificationTutorPartialForm
                values={data}
                onChangeNumberCompany={handleChangeNumberCompany}
                onChange={handleChange}
                errors={errors}
                dictNameLabel={translations['Site.Prime.Name']}
                dictSurnameLabel={translations['Site.Prime.Surname']}
                dictDenominationCompanyLabel={
                    translations['Site.Prime.DenominationCompany']
                }
                dictNumberCompanyLabel={
                    translations['Site.Prime.NumberCompany']
                }
                dictSocialCompanyLabel={
                    translations['Site.Prime.SocialCompany']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
            />

            <AddressPartialForm
                values={data}
                onChange={handleChange}
                onChangeNumberAddress={handleChangeNumberAddress}
                errors={errors}
                dictAddressStreetLabel={
                    translations['Site.Prime.AddressStreet']
                }
                dictAddressNumberLabel={
                    translations['Site.Prime.AddressNumber']
                }
                dictAddressPostalLabel={
                    translations['Site.Prime.AddressPostal']
                }
                dictAddressLocalityLabel={
                    translations['Site.Prime.AddressLocality']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
            />

            <ContactPartialForm
                values={data}
                onChange={handleChange}
                onChangePhone={handleChangePhone}
                onIbanCheck={handleBlurNumberCompanyVoid}
                errors={errors}
                dictMailLabel={translations['Site.Prime.MailCompany']}
                dictMailConfirmationLabel={
                    translations['Site.Prime.MailCompanyConfirmation']
                }
                dictPhoneLabel={translations['Site.Prime.PhoneCompany']}
                dictPhoneConfirmationLabel={
                    translations['Site.Prime.PhoneConfirmation']
                }
                dictIbanCompanyLabel={translations['Site.Prime.IbanCompany']}
                dictBicCompanyLabel={translations['Site.Prime.BicCompany']}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictMailConfirmationError={
                    translations['Site.Prime.MailCompanyConfirmationError']
                }
                dictPhoneConfirmationError={
                    translations['Site.Prime.PhoneConfirmationError']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                isIbanOk={data.iban.length >= 16}
                isBelgianIban={data.iban.startsWith('BE')}
            />

            <SectorPartialForm
                language={language}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictCommissionCompanyLabel={
                    translations['Site.Prime.CommissionCompany']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                values={data}
                errors={errors}
                onSelectSector={handleSelectSector}
                onChangeCommission={handleChangeCommission}
                onKeyDownNumber={(e) => {
                    if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== 'Backspace' &&
                        e.key !== 'Tab'
                    ) {
                        e.preventDefault();
                    }
                }}
            />

            <FloatLabel
                active={!!data.nationalRegisterTrainee}
                label={translations['Site.Prime.NationalRegisterTrainee']}
                labelId={'nationalRegisterTrainee'}
            >
                <input
                    type="text"
                    name="nationalRegisterTrainee"
                    value={data.nationalRegisterTrainee}
                    onChange={onChangeTraineeNiss}
                />
            </FloatLabel>
            {errors.nationalRegisterTrainee && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}
            {errors.nationalRegisterTraineeIsValid && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.IdentificationCodeError']}
                    </label>
                </div>
            )}

            <FloatLabel
                active={!!data.nationalRegisterTutor}
                label={translations['Site.Prime.NationalRegisterTutor']}
                labelId={'nationalRegisterTutor'}
            >
                <input
                    type="text"
                    name="nationalRegisterTutor"
                    value={data.nationalRegisterTutor}
                    onChange={onChangeTutorNiss}
                />
            </FloatLabel>
            {errors.nationalRegisterTutor && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}
            {errors.nationalRegisterTutorIsValid && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.IdentificationCodeError']}
                    </label>
                </div>
            )}

            <div className="col-12">
                <h3 className="h3">{translations['Site.Prime.TitleFiles']}</h3>
                <div className="row px-4">
                    <div className={'col-12 pb-4 border'} style={borderRadius}>
                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.BankAttestationFile'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.BankAttestationFileInfo'
                                    ]
                                }
                                file={data.bankAttestationFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={errors.bankAttestationFile}
                                errorLabel={
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="bankAttestationFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>

                        <div className="row">
                            <FileUploadField
                                label={
                                    translations[
                                        'Site.Prime.SchoolAttestationFile'
                                    ]
                                }
                                infoLabel={
                                    translations[
                                        'Site.Prime.SchoolAttestationFileInfo'
                                    ]
                                }
                                file={data.schoolAttestationFile}
                                noneLabel={translations['Site.Prime.NoneLabel']}
                                uploadLabel={translations['Site.Prime.Upload']}
                                error={
                                    errors.schoolAttestationFile &&
                                    translations['Site.Prime.ErrorSubmit']
                                }
                                fieldName="schoolAttestationFile"
                                onFileSelect={handleChangeFiles}
                                onFileDelete={onFileDelete}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <CheckboxField
                id="dataAreCorrect"
                name="dataAreCorrect"
                label={translations['Site.Prime.DataAreCorrectLabel']}
                value={data.dataAreCorrect}
                onChange={handleChange}
                error={
                    errors.dataAreCorrect &&
                    translations['Site.Prime.ErrorSubmit']
                }
                style={borderRadius}
            />

            <div className="col-12 text-right pt-3">
                <label className="alert-light">
                    {translations['Site.PrimePhoenix.MandatoryField']}
                </label>
            </div>

            <div className="form-group submit mt-5">
                <button type="submit" className="btn-block">
                    <i className="icon-check"></i>
                    {translations['Site.FondsRebond.Submit']}
                </button>
            </div>
        </form>
    );
};

WidgetHelper.register(PrimeTuteurForm, 'prime-tuteur-form-widget');
